import { useId } from 'react';
import { Badge } from '@/components/Badge';
import ActionButton from '@/components/Button/ActionButton';
import { Checkbox } from '@/components/Checkbox';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import type { PlaceListItem } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
import { MasterDataCombobox } from './MasterDataCombobox';
type Props = CFormItemProps & {
  value?: PlaceListItem[];
  onChange: (value: PlaceListItem[]) => void;
};
export const CFormPlaceMultiSelectWithPrimaryItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const idPrefix = useId();
  const values = props.value || [];
  const handleAdd = () => {
    props.onChange([...values, {
      placeId: '',
      placeName: '',
      isPrimary: values.length === 0
    }]);
  };
  const handleRemove = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    props.onChange(newValues);
  };
  const handleChange = (index: number, field: keyof PlaceListItem, value: string | boolean, label?: string) => {
    let newValues = values.map(v => ({
      ...v
    }));
    if (field === 'isPrimary' && value === true) {
      newValues = newValues.map((v, i) => ({
        ...v,
        isPrimary: i === index
      }));
    } else if (field === 'placeId') {
      newValues[index] = {
        ...newValues[index],
        placeId: value as string,
        placeName: label || ''
      };
    } else {
      newValues[index] = {
        ...newValues[index],
        [field]: value
      };
    }
    props.onChange(newValues);
  };
  const renderReadMode = () => {
    if (values.length === 0) return <Text variant="p">-</Text>;
    return <VStack className="gap-2 w-full">
          {values.map((item, index) => <HStack key={`${idPrefix}-${index}`} className="items-center gap-2">
              <Text variant="labelLarge">{item.placeName || '-'}</Text>
              {item.isPrimary && <Badge variant="secondary">{tc('primary')}</Badge>}
            </HStack>)}
        </VStack>;
  };
  const renderWriteMode = () => {
    return <VStack className="gap-2 w-full">
          {values.length > 0 && <HStack className="items-center w-full gap-4 ml-1">
              <div className="flex-1 justify-start">
                <Text variant="tableHead">{props.label}</Text>
              </div>
              <div className="w-[80px] justify-center flex">
                <Text variant="tableHead">{tc('primary')}</Text>
              </div>
              <div className="w-10" />
            </HStack>}

          {values.map((item, index) => <HStack key={`${idPrefix}-${index}`} className="items-center w-full gap-4 ml-1">
              <div className="flex-1 min-w-0">
                <MasterDataCombobox value={item.placeId ?? undefined} displayValue={item.placeName ?? undefined} onChange={(val, label) => handleChange(index, 'placeId', val || '', label)} apiPath="/v2/places" queryKeyPrefix="places" disabled={props.readonly} />
              </div>
              <div className="w-[80px] justify-center flex">
                <Checkbox checked={item.isPrimary} onCheckedChange={checked => {
            if (!checked) return;
            handleChange(index, 'isPrimary', true);
          }} disabled={props.readonly} />
              </div>
              <div className="w-10">
                <ActionButton icon="Trash2Icon" variant="ghost" iconColor="muted" onClick={() => handleRemove(index)} disabled={props.readonly || props.required !== false && item.isPrimary} aria-label={tc('action.delete')} />
              </div>
            </HStack>)}

          {!props.readonly && <HStack className="items-left">
              <ActionButton className="pl-0" icon="PlusIcon" onClick={handleAdd} variant="ghost" iconColor="primary">
                {tc('action.add')}
              </ActionButton>
            </HStack>}
        </VStack>;
  };
  return <CFormItemBase {...props} readComponent={renderReadMode()} writeComponent={renderWriteMode()} />;
});